import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Clock, MapPin, User, XCircle } from 'lucide-react';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const VisitCard = ({ visit, isDragging, onClick }) => {
  const statusColors = {
    SCHEDULED: 'border-l-slate-400',
    IN_PROGRESS: 'border-l-amber-500',
    DONE: 'border-l-emerald-500'
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white p-3 border border-slate-200 shadow-sm cursor-pointer hover:border-orange-400 hover:shadow-md transition-all border-l-4 ${
        statusColors[visit.status] || 'border-l-slate-400'
      } ${isDragging ? 'opacity-50' : ''}`}
      data-testid={`visit-card-${visit.id}`}
    >
      <div className="space-y-2">
        <h4 className="font-semibold text-slate-900 text-sm">{visit.client_name}</h4>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <MapPin className="w-3 h-3" />
          <span>{visit.location_city}, {visit.location_postcode}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Clock className="w-3 h-3" />
          <span>{format(new Date(visit.scheduled_start), 'HH:mm')}</span>
          <span className="text-slate-400">•</span>
          <span>{visit.estimated_duration_minutes}min</span>
        </div>
        {visit.notes && (
          <p className="text-xs text-slate-500 mt-2">{visit.notes}</p>
        )}
      </div>
    </div>
  );
};

const DraggableVisitCard = ({ visit, onCardClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: visit.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = (e) => {
    if (!isDragging) {
      onCardClick(visit);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <VisitCard visit={visit} isDragging={isDragging} onClick={handleClick} />
    </div>
  );
};

export default function Dispatch() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [cleaners, setCleaners] = useState([]);
  const [editFormData, setEditFormData] = useState({
    assigned_cleaner_id: '',
    scheduled_start: '',
    estimated_duration_minutes: 60,
    notes: ''
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchBoard();
    fetchCleaners();
  }, [date]);

  const fetchCleaners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/cleaners`);
      setCleaners(response.data);
    } catch (error) {
      console.error('Failed to fetch cleaners');
    }
  };

  const fetchBoard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dispatch?date=${date}`);
      setBoard(response.data);
    } catch (error) {
      toast.error('Failed to fetch dispatch board');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const visitId = active.id;
    const targetColumn = over.id;

    if (targetColumn === 'unassigned') {
      try {
        await axios.patch(`${API_URL}/api/visits/${visitId}/assign`, {
          assigned_cleaner_id: null
        });
        toast.success('Visit unassigned');
        fetchBoard();
      } catch (error) {
        toast.error('Failed to update visit');
      }
    } else {
      try {
        await axios.patch(`${API_URL}/api/visits/${visitId}/assign`, {
          assigned_cleaner_id: targetColumn
        });
        toast.success('Visit assigned');
        fetchBoard();
      } catch (error) {
        toast.error('Failed to assign visit');
      }
    }
  };

  const changeDate = (days) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    setDate(format(newDate, 'yyyy-MM-dd'));
  };

  const handleCardClick = (visit) => {
    setSelectedVisit(visit);
    setEditFormData({
      assigned_cleaner_id: visit.assigned_cleaner_id || '',
      scheduled_start: format(new Date(visit.scheduled_start), "yyyy-MM-dd'T'HH:mm"),
      estimated_duration_minutes: visit.estimated_duration_minutes,
      notes: visit.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateVisit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        assigned_cleaner_id: editFormData.assigned_cleaner_id || null,
        scheduled_start: new Date(editFormData.scheduled_start).toISOString(),
        estimated_duration_minutes: editFormData.estimated_duration_minutes,
        notes: editFormData.notes
      };
      
      await axios.patch(`${API_URL}/api/visits/${selectedVisit.id}`, updateData);
      toast.success('Visit updated successfully');
      setEditDialogOpen(false);
      fetchBoard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visit');
    }
  };

  const handleCancelVisit = async () => {
    try {
      await axios.patch(`${API_URL}/api/visits/${selectedVisit.id}/cancel`);
      toast.success('Visit cancelled');
      setCancelDialogOpen(false);
      setEditDialogOpen(false);
      fetchBoard();
    } catch (error) {
      console.error('Cancel visit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel visit');
    }
  };

  const openCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const activeVisit = activeId
    ? board?.columns.flatMap(c => c.visits).concat(board?.unassigned || []).find(v => v.id === activeId)
    : null;

  // Calculate today overview stats from existing board data
  const todayStats = React.useMemo(() => {
    if (!board) return { total: 0, unassigned: 0, inProgress: 0, done: 0, plannedMinutes: 0 };
    
    const allVisits = [...(board.unassigned || []), ...(board.columns?.flatMap(c => c.visits) || [])];
    return {
      total: allVisits.length,
      unassigned: board.unassigned?.length || 0,
      inProgress: allVisits.filter(v => v.status === 'IN_PROGRESS').length,
      done: allVisits.filter(v => v.status === 'DONE').length,
      plannedMinutes: allVisits.reduce((sum, v) => sum + (v.estimated_duration_minutes || 0), 0)
    };
  }, [board]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="dispatch-page">
        <div className="flex justify-end items-center">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => changeDate(-1)} data-testid="prev-day-button">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40"
              data-testid="date-picker"
            />
            <Button variant="outline" onClick={() => changeDate(1)} data-testid="next-day-button">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="today-button"
            >
              Today
            </Button>
          </div>
        </div>

        {/* Today Overview Stats */}
        {board && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="today-overview">
            <div className="bg-white border border-slate-200 px-3 py-2 sm:px-4 sm:py-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Total</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-900">{todayStats.total}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2 sm:px-4 sm:py-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Unassigned</div>
              <div className="text-xl sm:text-2xl font-bold text-orange-500">{todayStats.unassigned}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2 sm:px-4 sm:py-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide">In Progress</div>
              <div className="text-xl sm:text-2xl font-bold text-amber-500">{todayStats.inProgress}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2 sm:px-4 sm:py-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Done</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-500">{todayStats.done}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2 sm:px-4 sm:py-3 col-span-2 sm:col-span-1">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Planned</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-700">{Math.floor(todayStats.plannedMinutes / 60)}h {todayStats.plannedMinutes % 60}m</div>
            </div>
          </div>
        )}

        {loading ? (
          <div>Loading...</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <SortableContext
                id="unassigned"
                items={board?.unassigned?.map(v => v.id) || []}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className="bg-slate-50 border border-slate-200 min-h-[500px] flex flex-col p-4 gap-3"
                  data-testid="unassigned-column"
                >
                  <div className="font-bold text-sm uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-200">
                    Unassigned ({board?.unassigned?.length || 0})
                  </div>
                  <div className="flex-1 space-y-3">
                    {board?.unassigned?.map((visit) => (
                      <DraggableVisitCard key={visit.id} visit={visit} onCardClick={handleCardClick} />
                    ))}
                  </div>
                </div>
              </SortableContext>

              {board?.columns.map((column) => (
                <SortableContext
                  key={column.cleaner_id}
                  id={column.cleaner_id}
                  items={column.visits.map(v => v.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    className="bg-slate-50 border border-slate-200 min-h-[500px] flex flex-col p-4 gap-3"
                    data-testid={`cleaner-column-${column.cleaner_id}`}
                  >
                    <div className="pb-2 border-b border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="font-bold text-sm text-slate-900">{column.cleaner_name}</span>
                      </div>
                      <div className="text-xs text-slate-500">{column.visits.length} visits</div>
                    </div>
                    <div className="flex-1 space-y-3">
                      {column.visits.map((visit) => (
                        <DraggableVisitCard key={visit.id} visit={visit} onCardClick={handleCardClick} />
                      ))}
                    </div>
                  </div>
                </SortableContext>
              ))}
            </div>

            <DragOverlay>
              {activeVisit ? <VisitCard visit={activeVisit} /> : null}
            </DragOverlay>
          </DndContext>
        )}

        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Visit</DialogTitle>
            </DialogHeader>
            {selectedVisit && (
              <form onSubmit={handleUpdateVisit} className="space-y-4" data-testid="edit-visit-form">
                <div className="bg-slate-50 border border-slate-200 p-4 mb-4">
                  <div className="text-sm font-semibold text-slate-900">{selectedVisit.client_name}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    {selectedVisit.location_address}, {selectedVisit.location_city} {selectedVisit.location_postcode}
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_assigned_cleaner_id">Assigned Cleaner</Label>
                  <Select
                    value={editFormData.assigned_cleaner_id || undefined}
                    onValueChange={(value) => setEditFormData({ ...editFormData, assigned_cleaner_id: value })}
                  >
                    <SelectTrigger data-testid="edit-cleaner-select">
                      <SelectValue placeholder="Leave unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {cleaners.filter(c => c.id).map((cleaner) => (
                        <SelectItem key={cleaner.id} value={cleaner.id}>
                          {cleaner.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit_scheduled_start">Scheduled Start</Label>
                  <Input
                    id="edit_scheduled_start"
                    type="datetime-local"
                    value={editFormData.scheduled_start}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduled_start: e.target.value })}
                    required
                    data-testid="edit-start-input"
                  />
                </div>

                <div>
                  <Label htmlFor="edit_estimated_duration_minutes">Duration (minutes)</Label>
                  <Input
                    id="edit_estimated_duration_minutes"
                    type="number"
                    value={editFormData.estimated_duration_minutes}
                    onChange={(e) => setEditFormData({ ...editFormData, estimated_duration_minutes: parseInt(e.target.value) })}
                    required
                    min="15"
                    step="15"
                    data-testid="edit-duration-input"
                  />
                </div>

                <div>
                  <Label htmlFor="edit_notes">Notes</Label>
                  <Textarea
                    id="edit_notes"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    rows={3}
                    data-testid="edit-notes-input"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openCancelDialog}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="cancel-visit-button"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Visit
                  </Button>
                  <div className="flex-1" />
                  <Button
                    type="submit"
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="update-visit-button"
                  >
                    Update Visit
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Visit</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this visit for "{selectedVisit?.client_name}"? This action will remove it from the dispatch board.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Visit</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleCancelVisit}
                className="bg-red-500 hover:bg-red-600"
              >
                Cancel Visit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
